import React from "react";

/**
 * Nature-themed Clariss Bangladesh reviews (pure React + Tailwind CSS)
 * - Mixed categories (Shampoo, Face Wash, Hair Color) — not all the same
 * - No external deps (no lucide-react, no framer-motion)
 * - Drop-in component: <ClarissReviewsNature />
 */

// ---- Demo data (replace with your real reviews) ----
const demoReviews = [
  {
    product: "Clariss Keratin Shampoo (Expert Series)",
    tag: "Shampoo",
    body: "Hair feels smoother and easier to manage. Frizz reduced and looks healthier after a week.",
    name: "Sadia Islam",
    city: "Dhaka",
    rating: 5,
    date: "Aug 2025",
    img: "https://i.ibb.co.com/vvh9Qy0W/download-2.jpg",
    accent: "from-emerald-100",
  },
  {
    product: "Clariss Lemon Face Wash",
    tag: "Face Wash",
    body: "Cleanses oil without drying. Skin looks fresh even in humid weather and feels very clean.",
    name: "Farzana Rahman",
    city: "Sylhet",
    rating: 5,
    date: "Aug 2025",
    img: "https://i.ibb.co.com/Ps2xpkqF/download.jpg",
    accent: "from-lime-100",
  },
  {
    product: "Clariss Hair Color — Natural Black (1.0) — Ammonia-Free",
    tag: "Hair Color",
    body: "Even coverage, no harsh smell, and a soft finish after rinse. Very comfortable on the scalp.",
    name: "Nusrat Jahan",
    city: "Chattogram",
    rating: 5,
    date: "Jul 2025",
    img: "https://i.ibb.co.com/7NzHx75g/download-1.jpg",
    accent: "from-green-100",
  },
];

// ---- Inline icons (no external deps) ----
const StarIcon = ({ filled = false, size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    className={filled ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
    aria-hidden="true"
  >
    <path d="M10 15.3 16.2 19l-1.64-7.03L20 7.24l-7.2-.61L10 0 7.2 6.63 0 7.24l5.46 4.73L3.8 19z" />
  </svg>
);

const QuoteIcon = ({ size = 20, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M7 6h5v5H9a2 2 0 0 0-2 2v1H4v-2a6 6 0 0 1 3-6Zm10 0h5v5h-3a2 2 0 0 0-2 2v1h-3v-2a6 6 0 0 1 3-6Z"
      fill="currentColor"
      opacity="0.6"
    />
  </svg>
);

const LeafIcon = ({ size = 18, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M21 3S12 2 7.5 6.5 3 21 3 21s12 .5 15.5-4S21 3 21 3Zm-8.5 4.5c.5 0 1 .4 1 1 0 4-2.5 6.5-6.5 6.5-.6 0-1-.5-1-1 0-4 2.5-6.5 6.5-6.5Z"
      fill="currentColor"
    />
  </svg>
);

// ---- Small bits ----
const Stars = ({ rating = 5, size = 16 }) => (
  <div
    className="flex items-center gap-0.5"
    aria-label={`${rating} out of 5 stars`}
  >
    {Array.from({ length: 5 }).map((_, i) => (
      <StarIcon key={i} size={size} filled={i < rating} />
    ))}
  </div>
);

const Avatar = ({ name, img }) => {
  const initials = (name || "")
    .split(" ")
    .map((n) => n[0] || "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
  if (img && img.trim()) {
    return (
      <img
        src={img}
        alt={`${name} – reviewer`}
        className="h-12 w-12 rounded-xl object-cover ring-2 ring-green-900"
        loading="lazy"
      />
    );
  }
  return (
    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-400 to-green-600 grid place-items-center ring-2 ring-green-900">
      <span className="text-white font-semibold">{initials}</span>
    </div>
  );
};

// ---- Card ----
const ReviewCard = ({
  product,
  body,
  name,
  city,
  rating,
  img,
  date,
  tag,
  accent = "from-green-100",
}) => {
  return (
    <article className=" relative  overflow-hidden border-l-2 border-[#15803D] bg-white/95 shadow-sm hover:shadow-md transition-shadow">
      {/* Nature glow blobs */}
      <div
        className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-green-50 blur-2xl"
        aria-hidden
      />
      <div
        className="absolute -left-10 -bottom-10 h-28 w-28 rounded-full bg-lime-50 blur-2xl"
        aria-hidden
      />

      <div className="p-6 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Avatar name={name} img={img} />
            <div className="leading-tight">
              <p className="font-semibold text-gray-900">{name}</p>
              <p className="text-xs text-gray-500">
                {city || "Bangladesh"}
                {date ? ` • ${date}` : ""}
              </p>
            </div>
          </div>
          <div className="inline-flex items-center gap-1 text-green-700 bg-green-50 border border-green-100 rounded-full px-2 py-1 text-[11px]">
            <LeafIcon />
            <span>Verified</span>
          </div>
        </div>

        {tag && (
          <span className="self-start text-[11px] font-medium text-green-700 bg-green-50 border border-green-100 rounded-full px-2 py-0.5">
            {tag}
          </span>
        )}

        <h3 className="text-base md:text-lg font-semibold text-gray-900">
          {product}
        </h3>

        <div className="relative">
          <QuoteIcon className="absolute -left-1 -top-1 text-green-900" />
          <p className="pl-6 text-gray-700 leading-relaxed">{body}</p>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <Stars rating={rating} />
          <div className="text-xs text-gray-500">— {name}</div>
        </div>
      </div>
    </article>
  );
};

// ---- Header ----
const SectionHeader = ({ title, subtitle }) => (
  <header className="text-center max-w-2xl mx-auto mb-10">
    <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
      <LeafIcon />
      Nature-Inspired Care
    </div>
    <h2 className="mt-3 ttext-xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
      {title}
    </h2>
    {subtitle && <p className="mt-3 text-gray-600">{subtitle}</p>}
  </header>
);

// ---- Background decoration ----
const NatureBackdrop = () => (
  <>
    <div
      className="absolute inset-0 bg-sec pointer-events-none"
      aria-hidden
    />
  </>
);

// ---- Main Component (JS) ----
const ClarissReviewsNature = ({
  reviews = demoReviews,
  title = "What Clariss Customers Say",
  subtitle = "Clean, nature-inspired formulas that deliver visible results.",
}) => {
  return (
    <section className="relative py-14 md:py-20 overflow-hidden">
      <NatureBackdrop />
      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader title={title} subtitle={subtitle} />
        <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r, idx) => (
            <ReviewCard key={`${r.name}-${idx}`} {...r} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClarissReviewsNature;
